import FormattedMessage from 'I18n/components/FormattedMessage';
import { FLAT } from 'customer-data-objects/product/Pricing';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { List } from 'foundations-components/transitional/components/List';
import FormattedPrice from 'payments-checkout-lib/components/Utilities/FormattedPrice';
import SharedLibLineItemUnitPrice from 'payments-checkout-lib/components/LineItems/LineItemUnitPrice';
import { getRecurringBillingFrequency } from 'payments-checkout-lib/utils/recurring';
import { useSubtotals } from '../../context/SubtotalsProvider';
import { getBundleAmount, getBundleBilling, getBundleDiscount, getBundleFirstPayment, getBundleTax, getLineItemAmount } from '../../utils/bundles';
import { isFutureBilledCheckoutLineItem } from '../../types/utils/typeguards';
import FirstPaymentDateSubtext from './FirstPaymentDateSubtext';
import LineItemCheckoutFee from './LineItemCheckoutFee';
import LineItemDescription from './LineItemDescription';
import LineItemDiscount from './LineItemDiscount';
import LineItemTax from './LineItemTax';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NumPayments = ({
  count
}) => /*#__PURE__*/_jsx(Text, {
  as: "small",
  color: "text-core-subtle",
  "data-test-id": "bundle-num-payments",
  children: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "payments.lineItemRecurringCharges.numPayments",
    options: {
      count
    }
  })
});
const BundleChild = ({
  currencyCode,
  lineItem,
  showPrice,
  showStartDate,
  numPayments
}) => /*#__PURE__*/_jsxs(Flex, {
  "data-test-id": "bundle-child",
  justifyContent: "space-between",
  alignItems: "start",
  children: [/*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    alignItems: "start",
    children: [showStartDate && isFutureBilledCheckoutLineItem(lineItem) && /*#__PURE__*/_jsx(FirstPaymentDateSubtext, {
      lineItem: lineItem
    }), /*#__PURE__*/_jsx(Text, {
      typography: "detail-200",
      "data-test-id": "bundle-child-name",
      children: lineItem.name
    })]
  }), showPrice && /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    alignItems: "end",
    children: [/*#__PURE__*/_jsx(Text, {
      typography: "detail-200",
      "data-test-id": "bundle-child-amount",
      children: /*#__PURE__*/_jsx(FormattedPrice, {
        currencyCode: currencyCode,
        price: getLineItemAmount(lineItem),
        frequency: getRecurringBillingFrequency(lineItem.recurringBillingTerms),
        currencyAndAmountStyle: {
          typography: 'heading-25'
        }
      })
    }), numPayments && /*#__PURE__*/_jsx(NumPayments, {
      count: numPayments
    })]
  })]
});
const BundleLineItem = ({
  currencyCode,
  parent,
  bundleChildren,
  isParentInSection = true,
  checkoutFeeAdjustment,
  hasFutureItemExpired = false
}) => {
  const {
    recurringSubtotals
  } = useSubtotals();
  const hasMultipleSubscriptions = recurringSubtotals.length > 1;
  const {
    isUniform,
    frequency,
    numPayments
  } = getBundleBilling(bundleChildren);
  const members = isParentInSection ? [parent, ...bundleChildren] : bundleChildren;
  const bundleAmount = getBundleAmount(members);
  const discount = getBundleDiscount(members);
  const tax = getBundleTax(members);
  const firstPayment = getBundleFirstPayment(members);
  const showsFixedNumPayments = (lineItems, count) => Boolean(count) && (hasMultipleSubscriptions || lineItems.some(isFutureBilledCheckoutLineItem));
  const bundleNumPayments = isUniform && showsFixedNumPayments(bundleChildren, numPayments) ? numPayments : null;
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "500",
    "data-test-id": "bundle-line-item",
    children: [/*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      children: [(firstPayment === null || firstPayment === void 0 ? void 0 : firstPayment.isUniform) && /*#__PURE__*/_jsx(FirstPaymentDateSubtext, {
        lineItem: firstPayment.lineItem
      }), /*#__PURE__*/_jsxs(Flex, {
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          alignItems: "start",
          flex: "0 0 55%",
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h6",
            "data-test-id": "bundle-name",
            marginBlock: "100",
            children: parent.name
          }), parent.description && /*#__PURE__*/_jsx(LineItemDescription, {
            description: parent.description
          }), parent.quantity > 1 && /*#__PURE__*/_jsx(SharedLibLineItemUnitPrice, {
            currencyCode: currencyCode,
            quantity: parent.quantity,
            effectiveUnitPrice: bundleAmount / parent.quantity,
            pricing: {
              pricingModel: FLAT,
              tierRanges: null,
              tierPrices: null,
              flatPrice: bundleAmount / parent.quantity
            }
          }), hasFutureItemExpired && /*#__PURE__*/_jsx(Text, {
            as: "small",
            color: "text-alert-default",
            marginBlockStart: "200",
            "data-test-id": "bundle-expired-payment",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "payments.futureCharges.expiredPayment"
            })
          })]
        }), /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          alignItems: "end",
          flex: "0 0 35%",
          children: [/*#__PURE__*/_jsx(Text, {
            "data-test-id": "bundle-amount",
            children: /*#__PURE__*/_jsx(FormattedPrice, {
              currencyCode: currencyCode,
              price: bundleAmount,
              frequency: isUniform ? frequency : null,
              currencyAndAmountStyle: {
                typography: 'body-125'
              }
            })
          }), discount && /*#__PURE__*/_jsx(LineItemDiscount, {
            currencyCode: currencyCode,
            discountAmount: discount.discountAmount,
            unadjustedAmount: discount.unadjustedAmount,
            value: discount.value,
            valueType: discount.valueType
          }), tax && /*#__PURE__*/_jsx(LineItemTax, {
            currencyCode: currencyCode,
            label: tax.label,
            taxAmount: tax.taxAmount
          }), checkoutFeeAdjustment && /*#__PURE__*/_jsx(LineItemCheckoutFee, {
            checkoutFeeAdjustment: checkoutFeeAdjustment
          }), bundleNumPayments && /*#__PURE__*/_jsx(NumPayments, {
            count: bundleNumPayments
          })]
        })]
      })]
    }), bundleChildren.length > 0 && /*#__PURE__*/_jsx(List, {
      wrapChildren: true,
      margin: "0",
      paddingInlineStart: "600",
      childStyles: {
        marginBlockStart: '400'
      },
      firstChildStyles: {
        marginBlockStart: '0'
      },
      children: bundleChildren.map(child => {
        var _child$recurringBilli, _child$recurringBilli2;
        const childNumPayments = (_child$recurringBilli = (_child$recurringBilli2 = child.recurringBillingTerms) === null || _child$recurringBilli2 === void 0 ? void 0 : _child$recurringBilli2.numPayments) !== null && _child$recurringBilli !== void 0 ? _child$recurringBilli : null;
        return /*#__PURE__*/_jsx(BundleChild, {
          currencyCode: currencyCode,
          lineItem: child,
          showPrice: !isUniform,
          showStartDate: !!firstPayment && !firstPayment.isUniform,
          numPayments: !isUniform && showsFixedNumPayments([child], childNumPayments) ? childNumPayments : null
        }, child.id);
      })
    })]
  });
};
export default BundleLineItem;